import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Link,
  Card,
  Button,
  Divider,
  Typography,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { fToNow } from '../../../utils/formatTime';
import Iconify from '../../../components/iconify';

AppNewsUpdate.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  list: PropTypes.array.isRequired,
};

function AppNewsUpdate({ title, subheader, list, ...other }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const displayedList = list.slice(0, 5);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <Card {...other} sx={{ height: 495, display: 'flex', flexDirection: 'column' }}>
      <CardHeader title={title} subheader={subheader} />

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Stack spacing={3} sx={{ p: 3 }}>
          {displayedList.map((news) => (
            <NewsItem key={news.id} news={news} />
          ))}
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ p: 2, textAlign: 'right' }}>
        <Button
          size="small"
          color="inherit"
          endIcon={<Iconify icon={'eva:arrow-ios-forward-fill'} />}
          onClick={handleDialogOpen}
        >
          View All
        </Button>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>All News</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ p: 1 }}>
            {list.map((news) => (
              <NewsItem key={news.id} news={news} />
            ))}
          </Stack>
          <Box sx={{ textAlign: 'right', pt: 2 }}>
            <Button onClick={handleDialogClose}>Close</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

NewsItem.propTypes = {
  news: PropTypes.shape({
    description: PropTypes.string,
    image: PropTypes.string,
    postedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    title: PropTypes.string,
  }),
};

function parseLinks(text) {
  const urlRegex = /((https?:\/\/[^\s]+))/g;
  return text.replace(
    urlRegex,
    (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
  );
}

function NewsItem({ news }) {
  const { image, title, description, postedAt } = news;

  return (
    <Stack direction="row" alignItems="flex-start" spacing={2}>
      <Box
        component="img"
        alt={title}
        src={image}
        sx={{ width: 48, height: 48, borderRadius: 1.5, flexShrink: 0 }}
      />

      <Box sx={{ flexGrow: 1 }}>
        <Link
          color="inherit"
          variant="subtitle2"
          underline="hover"
          sx={{ display: 'block', wordBreak: 'break-word' }}
        >
          {title}
        </Link>

        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', whiteSpace: 'pre-line', wordBreak: 'break-word' }}
          dangerouslySetInnerHTML={{ __html: parseLinks(description) }}
        />
      </Box>

      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', minWidth: '75px', textAlign: 'right', pt: '5px' }}
      >
        {fToNow(postedAt)}
      </Typography>
    </Stack>
  );
}

export default AppNewsUpdate;
